import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataTable, type DataTableFilter, type DataTableRefs } from '@odo/shared-ui';
import { useOrthodontists, useInfiniteOrthodontists } from '../../hooks/useOrthodontistAccounts';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DATA_TABLE_ROW_HEIGHT } from '../../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../../hooks/useViewportTableLimit';
import type { OrthodontistsSortBy } from '../../services/orthodontistAccounts';
import { PRACTICE_FIELD_OPTIONS } from '../../components/accounts/practice/details/fieldDefinitions';
import { OrthodontistsTable } from '../../components/accounts/orthodontists/OrthodontistsTable';

const SEARCH_DEBOUNCE_MS = 300;
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

type OrthodontistFilters = {
    /** Exact match against orthodontist.state - single-select, unlike
     * practices' `state` (the orthodontist endpoint only accepts one). */
    state: string | null;
};

export function OrthodontistsList() {
    usePageTitle('Orthodontists');
    const { setTitle, setSubtitle } = usePageHeader();
    const navigate = useNavigate();

    const table = useDataTable<OrthodontistsSortBy, OrthodontistFilters>({
        defaultSort: { by: 'name', order: 'asc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { state: null }
    });
    const { state: stateFilter } = table.filters;
    const isInfinite = table.mode === 'infinite';

    const [searchInput, setSearchInput] = useState('');
    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput, commitSearch]);

    const {
        isReady: pageSizeReady,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef
    } = useViewportTableLimit(table, { rowHeight: DATA_TABLE_ROW_HEIGHT });

    const { page, limit, sort, order } = table.params;
    const sharedOrthodontistParams = {
        limit,
        q: table.search || undefined,
        sort,
        order,
        state: stateFilter || undefined
    };
    const paged = useOrthodontists({
        ...sharedOrthodontistParams,
        page,
        enabled: !isInfinite && pageSizeReady
    });
    const infinite = useInfiniteOrthodontists({ ...sharedOrthodontistParams, enabled: isInfinite });

    const orthodontists = isInfinite ? infinite.orthodontists : paged.orthodontists;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;

    const totalPages = Math.max(1, Math.ceil(meta.total / limit));

    useEffect(() => {
        setTitle('Orthodontists');
        setSubtitle(`${meta.total} total`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [meta.total, setTitle, setSubtitle]);

    const openOrthodontist = (id: string) => {
        navigate(`/accounts/orthodontists/${id}`);
    };
    const goToNewOrthodontist = () => navigate('/accounts/orthodontists/new');

    const hasFilters = stateFilter !== null;
    const clearFilters = () => {
        setSearchInput('');
        table.setFilter('state', null);
    };

    const filters: DataTableFilter[] = [
        {
            key: 'state',
            label: 'State',
            align: 'center',
            options: PRACTICE_FIELD_OPTIONS.state,
            value: stateFilter,
            onChange: (value) => table.setFilter('state', value)
        }
    ];

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <OrthodontistsTable
                orthodontists={orthodontists}
                isLoading={isLoading}
                search={table.search}
                hasFilters={hasFilters}
                onClearFilters={clearFilters}
                onOpenOrthodontist={openOrthodontist}
                onAddOrthodontist={goToNewOrthodontist}
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode={table.mode}
                onModeChange={table.setMode}
                pagination={{
                    page: table.page,
                    totalPages,
                    total: meta.total,
                    onPage: table.setPage
                }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                searchConfig={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Search by name or doctor'
                }}
                filters={filters}
                refs={refs}
            />
        </div>
    );
}
